import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { Buffer } from "buffer";
import he from "he";
import React from "react";
import parse from "html-react-parser";
import { Link, Location, NavigateFunction, Params } from "react-router-dom";
import SpeechClassComponent from "../QuestionSpeech";
import Header from "../SharedComponents/Header/Header";
import LoginGuard, { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { withLocation } from "../SharedComponents/withLocation";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { withParamsHooks } from "../SharedComponents/withParamsHooks";
import { IAssignmentMetadata, InstructionsController, InstructionsControllerDelegate } from "./controller";

interface State {
  error: {
    message?: string;
  };
  data: {
    assignmentToken: string;
    answeredConsent: boolean;
    dueDate?: Date;
    title: string;
    instructions: string;
    consentTermId?: number;
    totalQuestions: number;
    instrumentId: string;
    allow_audio: boolean;
    type: string;
  };
  isLoading: boolean;
}

interface Props {
  locationHooks: Location;
  navigationHooks: NavigateFunction;
  params: Params;
}

export class Instructions extends React.Component<Props, State> implements InstructionsControllerDelegate {

  private controller: InstructionsController;

  constructor(props: Props) {
    super(props);
    this.controller = new InstructionsController(this);
    this.state = {
      error: {},
      data: {
        assignmentToken: "",
        answeredConsent: false,
        title: "",
        instructions: "",
        totalQuestions: 0,
        instrumentId: this.props.locationHooks.state.data.instrumentId,
        allow_audio: this.props.locationHooks.state.data.allow_audio,
        type: this.props.locationHooks.state.data.type,
      },
      isLoading: true
    };
  }

  componentDidMount(): void {
    this.controller.getAssignmentToken(
      localStorage.getItem("AccessToken")!,
      this.props.params.assignmentId!
    )
  }

  // Delegate
  genericError(message?: string): void {
    this.setState({
      error: {
        message:
          message ||
          "Não foi possível carregar os dados deste instrumento. Tente novamente.",
      },
      isLoading: false,
    });
  }

  applicationShouldLogout(): void {
    logout();
  }

  didGetAssignmentToken(token: string): void {

    const splitToken = token.split(".")[1];
    const tokenData = JSON.parse(Buffer.from(splitToken, "base64").toString("utf8"));
    this.setState({
      error: {},
      data: {
        ...this.state.data,
        assignmentToken: token,
        dueDate: new Date(tokenData.dueDate),
        answeredConsent: tokenData.answeredConsent
      }
    }, () => {
      this.controller.getAssignmentMetadata(
        localStorage.getItem("AccessToken")!,
        token
      )
    });
  }

  didGetAssignmentMetadata(metadata: IAssignmentMetadata): void {
    const newData = { ...this.state.data };
    newData.instructions = metadata.instructions;
    newData.consentTermId = metadata.consentTermId;
    newData.title = metadata.title;
    newData.totalQuestions = metadata.totalQuestions;
    newData.allow_audio = metadata.allow_audio;
    newData.type = metadata.type
    this.setState({
      data: newData,
      isLoading: false
    });
  }

  // Internals
  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderError() {
    if (!this.state.error.message) {
      return null;
    }

    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={4}
        mx={2}
        width="100%"
        maxWidth={600}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {this.state.error.message}
        </Alert>
      </Box>
    );
  }


  renderSpeechTextAnswer(text: string, auto?: boolean, css?: object | null) {

    return (
      <React.Fragment>
        <SpeechClassComponent text={text} auto={auto} css={css}/>
      </React.Fragment>
    );
  }

  cleanHtmlText(html: string | null | undefined) {
    // title/instructions are nullable at runtime (the DB columns are nullable and
    // the `string` State type lies). Guard before .replace() — this runs BEFORE the
    // null-safe parse() at the render site, so without it a null value white-screens.
    const textWithoutTags = (html ?? "").replace(/<[^>]*>/g, "");
    return he.decode(textWithoutTags);
  }

  renderInstructions() {
    const mountedStateData = {
      assignmentToken: this.state.data.assignmentToken,
      answeredConsent: this.state.data.answeredConsent,
      totalQuestions: this.state.data.totalQuestions,
      instrumentTitle: this.state.data.title,
      instrumentId: this.props.locationHooks.state.data.instrumentId,
      applicationId: this.props.locationHooks.state.data.applicationId,
      allow_audio: this.state.data.allow_audio,
      type: this.state.data.type,
    }
    const htmlTitleToText = this.cleanHtmlText(this.state.data.title)
    const htmlInstructionsToText = this.cleanHtmlText(this.state.data.instructions)

    const audioTitle = this.state.data.allow_audio ? this.renderSpeechTextAnswer(htmlTitleToText, false, {display: 'inline'}) : null;
    const audioInstructions = this.state.data.allow_audio ? this.renderSpeechTextAnswer(htmlInstructionsToText, false, {display: 'inline'}) : null;
    
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
        marginX={10}
      >
        <Typography marginTop={8} variant="h3" color="primary">
          {audioTitle ? audioTitle: null }
          {this.state.data.title}
        </Typography>
        <Typography marginTop={3} variant="h5">
          {`Disponível até ${this.state.data.dueDate?.toLocaleDateString()}`}
        </Typography>
        <Box marginTop={2}>
          {audioInstructions ? audioInstructions: null }
          {parse(this.state.data.instructions ?? "")}
        </Box>
        <Button
          variant="contained"
          component={Link}
          to={this.state.data.answeredConsent ? `/instrument/questions` : `/instrument/consent`}
          state={{ data: mountedStateData }}
          sx={{
            borderRadius: 10,
            marginTop: 8,
            marginBottom: 12,
            width: {
              xs: 200,
              sm: 200,
              md: 200
            },
            alignSelf: "center"
          }}
        >
          <Typography color="white" variant="button">
            {this.state.data.answeredConsent ? `Continuar` : `Iniciar`}
          </Typography>
        </Button>
      </Box>
    )
  }

  render() {
    return (
      <Box display="flex" flexDirection="column" width="100%" alignItems="center" justifyContent="center">
        <LoginGuard />
        <Header logoutEnabled={true} />
        {this.state.isLoading
          ? this.renderLoading()
          : this.state.error.message
            ? this.renderError()
            : this.renderInstructions()}
      </Box>
    )
  }
}

export default withLocation(
  withNavigationHooks(
    withParamsHooks(
      Instructions
    )
  )
);
